import { createContext, useContext, useState, useEffect } from 'react';
import { getMyCompanies } from '../lib/api';
import { useAuth } from './AuthContext';

const CompanyFilterContext = createContext();

export const useCompanyFilter = () => {
  const context = useContext(CompanyFilterContext);
  if (!context) {
    throw new Error('useCompanyFilter must be used within CompanyFilterProvider');
  }
  return context;
};

export const CompanyFilterProvider = ({ children }) => {
  const { user } = useAuth();
  const [allCompanies, setAllCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('all');
  const [loading, setLoading] = useState(true);

  // Determine which companies the user can access
  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'demo_admin';
  const userCompanyIds = user?.company_ids || [];

  // Filter companies based on user's access
  const companies = isSuperAdmin 
    ? allCompanies 
    : allCompanies.filter(c => userCompanyIds.includes(c.id));

  // Show dropdown only for super admin or users with multiple companies
  const showCompanyDropdown = isSuperAdmin || companies.length > 1;
  
  // Can show "All Companies" option only for super admin or users with multiple companies
  const canShowAllOption = isSuperAdmin || companies.length > 1;

  useEffect(() => {
    if (user) {
      loadCompanies();
    }
  }, [user]);

  // Auto-select first company for users with single company
  useEffect(() => {
    if (!loading && !isSuperAdmin && companies.length === 1) {
      setSelectedCompanyId(companies[0].id);
    }
    // For users with multiple companies, keep "all" as valid option (don't force first company)
  }, [loading, companies, isSuperAdmin]);

  const loadCompanies = async () => {
    try {
      const response = await getMyCompanies();
      setAllCompanies(response.data || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const selectedCompany = selectedCompanyId === 'all' 
    ? null 
    : companies.find(c => c.id === selectedCompanyId);

  // For non-super-admin, if "all" is selected, return all their company IDs for filtering
  const getCompanyIdParam = () => {
    if (selectedCompanyId === 'all') {
      // For super admin, return undefined to get all data
      if (isSuperAdmin) return undefined;
      // For users with single company, return that company
      if (companies.length === 1) return companies[0].id;
      // For users with multiple companies and "all" selected, return undefined
      // The getCompanyIdsParam will be used instead
      return undefined;
    }
    return selectedCompanyId;
  };

  // Get comma-separated company IDs for "All Companies" view (used for multi-company users)
  const getCompanyIdsParam = () => {
    if (selectedCompanyId === 'all' && !isSuperAdmin && companies.length > 1) {
      // Use filtered companies (only ones with accessible locations), not raw userCompanyIds
      return companies.map(c => c.id).join(',');
    }
    return undefined;
  };

  // Get all company IDs for the user (for multi-company queries)
  const getUserCompanyIds = () => {
    if (isSuperAdmin) return undefined; // Super admin sees all
    return userCompanyIds.length > 0 ? userCompanyIds : undefined;
  };

  const value = {
    companies,           // Filtered companies for current user
    allCompanies,        // All companies (for super admin reference)
    selectedCompanyId,
    setSelectedCompanyId,
    selectedCompany,
    loading,
    isSuperAdmin,
    canShowAllOption,
    showCompanyDropdown, // Whether to show the dropdown at all
    userCompanyIds,
    getCompanyIdParam,
    getCompanyIdsParam,  // NEW: Returns comma-separated company IDs for "All" view
    getUserCompanyIds,
    refreshCompanies: loadCompanies,
  };

  return (
    <CompanyFilterContext.Provider value={value}>
      {children}
    </CompanyFilterContext.Provider>
  );
};

export default CompanyFilterContext;
